import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { PageModule, ColorKeys, TextOverrides } from '../../../../../../types/working-model';
import { updatePageModule } from '../../../../../../store/actions/admin/create-event/session';
import EditableTextDraft from '../../../../../general-ui/editable-text/editable-text-draft';
import EditableTextSlim from "../../../../../general-ui/editable-text/editable-text-slim";
import { getTextValue, updateTranslateKey } from '../../../../../../utils/utils';
import '../../../../../live-event/session/session-modules/description/description.scss';
import './description.scss';
import { TemplateClassNames } from '../../../../../../types/template-layouts';
import { EContentColorOverrideNames } from '../../../session/editor/moduleControls/edit-section-colors-modal';
import { useTypedSelector } from '../../../../../../store/reducers/use-typed-selector';
import { ChecklistTitles } from '../../../../../../store/reducers/admin/assistant';
import { addChecklistItem } from '../../../../../../store/actions/authentication';
import { ParamsProps } from '../../../../../live-event/live-event';
import Controls from '../../session-module-controls/controls';
import { getStylingOverrides } from '../../../../../live-event/utils';

interface Props {
	page_module: PageModule;
	template: TemplateClassNames;
}

export default function Description({
	page_module, template
}: Props): JSX.Element | null {
	const { workingSession, token, user } = useTypedSelector(state => ({
		workingSession: state.CreateSessionReducer.workingSession,
		user: state.AuthReducer.user,
		token: state.AuthReducer.token,
	}));

	const dispatch = useDispatch();

	const { language } = useParams() as ParamsProps;

	const content = page_module?.content;
	const styling_overrides = page_module?.styling_overrides;
	const { backgroundColor, backgroundImg } = getStylingOverrides(styling_overrides);

	function handleTextChange(key: string) {
		return (text: string) => {
			if (!workingSession) return;
			dispatch(
				updatePageModule({
					...page_module,
					content: {
						...content,
						[key]: updateTranslateKey({
							translateString: page_module?.content?.[key],
							input: text,
							baseLanguage: workingSession.default_language,
							language
						}),
					},
				})
			);

			// ASSISTANT CHECKLIST ITEM: complete users checklist item for updating section
			if (
				user &&
				!user.onboarded.includes(ChecklistTitles.editSection)
			) dispatch(addChecklistItem(token!, user.id, user.onboarded, ChecklistTitles.editSection));
		};
	}

	function handleStyleOverrideChange(key: string) {
		return (overrides: TextOverrides) => {
			const updatedOverrides = {
				...styling_overrides,
				content: {
					...styling_overrides?.content,
					[key]: overrides
				}
			};
			dispatch(
				updatePageModule({
					...page_module,
					styling_overrides: updatedOverrides
				})
			);

			// ASSISTANT CHECKLIST ITEM: complete users checklist item for updating section
			if (
				user &&
				!user.onboarded.includes(ChecklistTitles.editSection)
			) dispatch(addChecklistItem(token!, user.id, user.onboarded, ChecklistTitles.editSection));
		};
	}
	const { custom_heading, description, title } = getTextValue(content, language);

	return page_module.is_on && (!workingSession?.layout?.withMenu || page_module) ? (
		<div
			className={classNames(
				"editor-wrapper description",
				backgroundColor,
				template
			)}
			style={backgroundImg}
		>
			<Controls
				page_module={page_module}
				openEditModal={() => ({})}
				display={{ editColors: true, changeSectionPosition: true }}
			/>
			<div className="container" style={backgroundImg}>
				<EditableTextSlim
					tagType="h5"
					className="evt-heading-5 custom-title"
					value={custom_heading}
					onChange={handleTextChange('custom_heading')}
					defaultColor={ColorKeys.bodyTextColor}
					onOverrideChange={handleStyleOverrideChange('custom_heading')}
					elementName={EContentColorOverrideNames.custom_heading}
					colorOverride={styling_overrides?.content?.custom_heading?.color}
				/>
				<div className="description-content">
					<EditableTextSlim
						tagType="h3"
						className="evt-heading-3 title"
						value={title}
						onChange={handleTextChange('title')}
						defaultColor={ColorKeys.bodyTextColor}
						onOverrideChange={handleStyleOverrideChange('title')}
						elementName={EContentColorOverrideNames.title}
						colorOverride={styling_overrides?.content?.title?.color}
					/>
					<EditableTextDraft
						className="evt-body-text-1 description"
						value={description}
						onChange={handleTextChange('description')}
						defaultColor={ColorKeys.bodyTextColor}
						onOverrideChange={handleStyleOverrideChange('description')}
						elementName={EContentColorOverrideNames.description}
						colorOverride={styling_overrides?.content?.description?.color}
						placeholder={"Enter description..."}
					/>
				</div>
			</div>
		</div>
	) : null;
}
